import classNames from "classnames";
import React, {
    ReactNode,
} from "react";

import {
    RiAddLine, RiAlertLine, RiArrowLeftLine, RiArrowRightLine, RiDeleteBinLine, RiEdit2Line, RiFlashlightLine,
    RiMoreLine,
    RiSearchLine,
    RiSendPlaneLine,
    RiSparklingLine,
    RiSubtractLine,
    RiZoomInLine,
    RiZoomOutLine,
} from "@remixicon/react";
import { ReactComponent as DraggableIcon } from "../../../assets/icons/draggable.svg";
import { ReactComponent as VariablesIcon } from "../../../assets/icons/variables.svg";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close.svg";
import { ReactComponent as AutomationActionIcon } from "../../../assets/icons/actions.svg";
import { ReactComponent as LinkIcon } from "../../../assets/icons/link.svg";
import { ReactComponent as CircleIcon } from "../../../assets/icons/circle.svg";
import { ReactComponent as CheckmarkIcon } from "../../../assets/icons/checkmark.svg";
import { ReactComponent as AppsLineIntegration } from "../../../assets/icons/apps-line-integrations.svg";
import { ReactComponent as RocketIcon } from "../../../assets/icons/rocket-new.svg";
import { ReactComponent as GroupsList } from "../../../assets/icons/groups-list.svg";

export type IconSize = "xsmall" | "small" | "medium" | "large" | "xlarge" | "xxlarge" | "auto";
export type IconVariant = "primary"
| "default" | "secondary"
| "subtle" | "link"
| "link-hover" | "tertiary"
| "warning" | "danger" | "success";
export type IconBackground = "primary" | "warning";

export interface IconProps {
    icon: ReactNode;
    /* eslint-disable react/require-default-props */
    size?: IconSize;
    variant?: IconVariant
    className?: string;
    background?: IconBackground;
    isRounded?: boolean;
    alt?: string;
    /* eslint-enable react/require-default-props */
}

function Icon({
    variant, className, icon, size, alt, background, isRounded,
}: IconProps): ReactNode {
    return (
        <span
            className={classNames(
                "icon",
                `icon-${variant}`,
                `icon-${size}`,
                background ? `icon-bg-${background} icon-centered` : "",
                isRounded ? "icon-rounded" : "",
                className,
            )}
            aria-description={alt}
        >
            {icon}
        </span>

    );
}

Icon.defaultProps = {
    variant: "md",
    className: "",
    size: "small",
    alt: "Default Icon",
    isRounded: false,
    background: undefined,
};

const Icons = {
    Draggable: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<DraggableIcon />} />,
    Edit: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<RiEdit2Line />} />,
    Delete: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<RiDeleteBinLine />} />,
    Add: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<RiAddLine />} />,
    Subtract: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<RiSubtractLine />} />,
    Automation: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<RiFlashlightLine />} />,
    AutomationAction: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<AutomationActionIcon />} />,
    Alert: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<RiAlertLine />} />,
    Variable: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<VariablesIcon />} />,
    Close: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<CloseIcon />} />,
    Circle: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<CircleIcon />} />,
    Checkmark: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<CheckmarkIcon />} />,
    Send: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<RiSendPlaneLine />} />,
    Next: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<RiArrowRightLine />} />,
    Back: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<RiArrowLeftLine />} />,
    Link: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<LinkIcon />} />,
    More: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<RiMoreLine />} />,
    ArtificialIntelligence: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<RiSparklingLine />} />,
    Search: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<RiSearchLine />} />,
    ZoomIn: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<RiZoomInLine />} />,
    ZoomOut: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<RiZoomOutLine />} />,
    Integrations: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<AppsLineIntegration />} />,
    Rocket: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<RocketIcon />} />,
    ChannelTag: (props: Omit<IconProps, "icon">) => <Icon {...props} icon={<GroupsList />} />,
};

const defaultIcon = Icon as unknown as Record<string, unknown>;
Object.entries(Icons).forEach(([key, value]) => {
    defaultIcon[key] = value;
    (defaultIcon[key] as {defaultProps: typeof Icon.defaultProps}).defaultProps = Icon.defaultProps;
});

export default Icon as typeof Icon & typeof Icons;
